import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import { Check, ChevronDown, X } from "lucide-react";

/**
 * MultiSelectFilter — checkbox-list popover.
 * Props:
 *   label: pill label e.g. "Status"
 *   value: array of selected strings
 *   options: array of { value, label } OR array of strings
 *   onChange: (newArray) => void
 *   testId: data-testid
 *   width: trigger width class (default "w-[150px]")
 */
export default function MultiSelectFilter({ label, value = [], options = [], onChange, testId, width = "w-[150px]" }) {
  const [open, setOpen] = useState(false);
  const opts = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const selected = value || [];

  const toggle = (v) => {
    if (selected.includes(v)) onChange(selected.filter((x) => x !== v));
    else onChange([...selected, v]);
  };
  const clear = (e) => { e.stopPropagation(); onChange([]); };

  const trigger = selected.length === 0
    ? label
    : selected.length === 1
      ? `${label}: ${opts.find((o) => o.value === selected[0])?.label || selected[0]}`
      : `${label}: ${selected.length} selected`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={`${width} h-10 justify-between text-xs font-normal px-3`}
          data-testid={testId}
        >
          <span className="truncate">{trigger}</span>
          <span className="flex items-center gap-1 shrink-0">
            {selected.length > 0 && (
              <X className="w-3 h-3 opacity-60 hover:opacity-100" onClick={clear} />
            )}
            <ChevronDown className="w-3.5 h-3.5 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1 max-h-72 overflow-y-auto" align="start">
        {opts.length === 0 && (
          <div className="p-3 text-xs text-muted-foreground">No options</div>
        )}
        {opts.map((o) => {
          const checked = selected.includes(o.value);
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => toggle(o.value)}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-muted text-left"
              data-testid={`${testId}-option-${o.value}`}
            >
              <span className={`w-4 h-4 rounded border ${checked ? "bg-primary border-primary text-primary-foreground" : "border-border"} flex items-center justify-center shrink-0`}>
                {checked && <Check className="w-3 h-3" />}
              </span>
              <span className="truncate">{o.label}</span>
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
